<template>
  <div class="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8 font-sans text-gray-800 dashboard-finance">
    <div class="max-w-7xl mx-auto">

      <header class="mb-6">
        <div class="flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">Dashboard Financeiro</h1>
            <p class="text-gray-600">Resumo das atividades do guichê.</p>
          </div>
          <caixa-status :status="caixaStatus" />
        </div>
      </header>

      <div class="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-wrap items-center gap-4">
        <div class="flex-1 min-w-[200px]">
          <label for="date" class="block text-sm font-medium text-gray-700 mb-1">Data</label>
          <input type="date" id="date" v-model="filters.date"
            class="w-full h-12 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div class="flex-1 min-w-[200px]">
          <label for="agency" class="block text-sm font-medium text-gray-700 mb-1">Agência</label>
          <select id="agency" v-model="filters.agency"
            class="w-full h-12 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-auto">
            <option value="all">Todas as Agências</option>
            <option v-for="agency in agencias" :key="agency.id" :value="agency.id">{{ agency.nome }}</option>
          </select>
        </div>
        <div class="flex-1 min-w-[200px]">
          <label for="operator" class="block text-sm font-medium text-gray-700 mb-1">Operador</label>
          <select id="operator" v-model="filters.operator"
            class="w-full h-12 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-auto">
            <option value="all">Todos os Operadores</option>
            <option v-for="operator in operadoresFiltrados" :key="operator.id" :value="operator.id">{{
              operator.nome_completo
            }}</option>
          </select>
        </div>
        <div class="self-end">
          <button @click="resetFilters"
            class="w-full sm:w-auto p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center gap-2 h-full mt-1 sm:mt-0">
            <i data-lucide="filter-x" style="width: 18px; height: 18px;"></i>
            Limpar
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

        <fluxo-caixa-card :data="displayData.entradasSaidas" />

        <section-card icon="ticket" title="Passagens Vendidas" :quantity="displayData.passagens.quantidade"
          unit="unidades">
          <div class="mt-4">
            <p class="text-gray-500 text-sm">Valor Total</p>
            <p class="text-3xl font-bold text-blue-600">R$ {{ totalPassagens.toLocaleString('pt-BR', {
              minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</p>
            <payment-breakdown :dinheiro="displayData.passagens.dinheiro" :cartao="displayData.passagens.cartao"
              :pix="displayData.passagens.pix" />
          </div>
        </section-card>

        <section-card icon="package" title="Encomendas" :quantity="displayData.encomendas.quantidade"
          unit="despachadas">
          <div class="mt-4">
            <p class="text-gray-500 text-sm">Valor Total</p>
            <p class="text-3xl font-bold text-blue-600">R$ {{ totalEncomendas.toLocaleString('pt-BR', {
              minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</p>
            <payment-breakdown :dinheiro="displayData.encomendas.dinheiro" :cartao="displayData.encomendas.cartao"
              :pix="displayData.encomendas.pix" />
          </div>
        </section-card>

        <section-card icon="baggage-claim" title="Excesso de Bagagem" :quantity="displayData.excessoBagagem.quantidade"
          unit="ocorrências">
          <div class="mt-4">
            <p class="text-gray-500 text-sm">Valor Total</p>
            <p class="text-3xl font-bold text-blue-600">R$ {{ totalBagagem.toLocaleString('pt-BR', {
              minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</p>
            <payment-breakdown :dinheiro="displayData.excessoBagagem.dinheiro"
              :cartao="displayData.excessoBagagem.cartao" :pix="displayData.excessoBagagem.pix" />
          </div>
        </section-card>

        <section-card icon="repeat" title="Movimentações Avulsas" :quantity="displayData.avulsas.quantidade"
          unit="transações">
          <div class="mt-4 space-y-2">
            <div>
              <p class="text-gray-500 text-sm">Total de Entradas</p>
              <p class="text-xl font-bold text-green-600">+ R$ {{
                displayData.avulsas.totalEntradas.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                maximumFractionDigits: 2 }) }}</p>
            </div>
            <div>
              <p class="text-gray-500 text-sm">Total de Saídas</p>
              <p class="text-xl font-bold text-red-600">- R$ {{ displayData.avulsas.saidas.toLocaleString('pt-BR', {
                minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</p>
            </div>
          </div>
        </section-card>

        <grand-totals-dashboard :dinheiro="grandTotalDinheiro" :cartao="grandTotalCartao" :pix="grandTotalPix"
          :total-geral="grandTotalGeral" />
      </div>
    </div>
  </div>
</template>

<script>
// Subcomponentes locais
// CaixaStatus
const CaixaStatus = {
  name: 'CaixaStatus',
  props: ['status'],
  data() {
    return {
      statusInfo: {
        Aberto: { text: 'Caixa Aberto', color: 'bg-green-500', textColor: 'text-green-800', bgColor: 'bg-green-100' },
        Fechado: { text: 'Caixa Fechado', color: 'bg-red-500', textColor: 'text-red-800', bgColor: 'bg-red-100' },
      }
    }
  },
  render(h) {
    if (!this.status) {
      return h('div', { class: 'bg-gray-100 p-3 rounded-lg text-center text-sm text-gray-600 border border-dashed' }, [
        h('p', 'Selecione agência e operador para ver o status.')
      ]);
    }
    const info = this.statusInfo[this.status];
    return h('div', { class: ['p-3 rounded-lg flex flex-grow-0 items-center space-x-3 transition-all duration-300', info.bgColor] }, [
      h('span', { class: ['w-3 h-3 rounded-full animate-pulse', info.color] }),
      h('span', { class: ['font-bold', info.textColor] }, info.text)
    ]);
  }
};

// PaymentBreakdown
const PaymentBreakdown = {
  name: 'PaymentBreakdown',
  props: ['dinheiro', 'cartao', 'pix'],
  render(h) {
    const createItem = (icon, value) =>
      h('div', { class: 'flex items-center text-gray-600' }, [
        h('i', { attrs: { 'data-lucide': icon }, class: 'w-4 h-4 mr-1.5 text-blue-500' }),
        h('span', { class: 'font-medium text-gray-800' }, `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
      ]);
    return h('div', { class: 'mt-4 flex flex-wrap justify-start gap-x-4 gap-y-2 text-sm' }, [
      createItem('dollar-sign', this.dinheiro),
      createItem('credit-card', this.cartao),
      createItem('qr-code', this.pix)
    ]);
  }
};

// SectionCard
const SectionCard = {
  name: 'SectionCard',
  props: ['icon', 'title', 'quantity', 'unit'],
  render(h) {
    return h('div', { class: 'bg-white p-6 rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300' }, [
      h('div', { class: 'flex items-center space-x-4' }, [
        h('div', { class: 'p-3 rounded-full bg-blue-100' }, [
          h('i', { attrs: { 'data-lucide': this.icon }, class: 'w-6 h-6 text-blue-600' })
        ]),
        h('div', [
          h('h3', { class: 'text-lg font-semibold text-gray-800' }, this.title),
          h('p', { class: 'text-2xl font-bold text-gray-900' }, [
            `${this.quantity} `,
            h('span', { class: 'text-base font-normal text-gray-500' }, this.unit)
          ])
        ])
      ]),
      this.$slots.default
    ]);
  }
};

// FluxoCaixaCard
const FluxoCaixaCard = {
  name: 'FluxoCaixaCard',
  props: ['data'],
  computed: {
    totalEntradas() { return this.data.filter(d => d.tipo === 'entrada').reduce((acc, i) => acc + parseFloat(i.valor), 0); },
    totalSaidas() { return this.data.filter(d => d.tipo === 'saida').reduce((acc, i) => acc + parseFloat(i.valor), 0); },
    saldo() { return this.totalEntradas - this.totalSaidas; }
  },
  render(h) {
    const createItem = (item) =>
      h('div', { key: item.id, class: 'flex justify-between items-center bg-gray-50 hover:bg-gray-100 p-3 rounded-lg' }, [
        h('div', { class: 'flex items-center' }, [
          item.tipo === 'entrada'
            ? h('i', { attrs: { 'data-lucide': 'arrow-up-right' }, class: 'w-5 h-5 text-green-600 mr-3' })
            : h('i', { attrs: { 'data-lucide': 'arrow-down-left' }, class: 'w-5 h-5 text-red-600 mr-3' }),
          h('span', { class: 'text-gray-700' }, item.descricao)
        ]),
        h('span', { class: ['font-bold', item.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'] },
          `R$ ${parseFloat(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        )
      ]);

    return h('div', { class: 'bg-white p-6 rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 col-span-1 md:col-span-2 lg:col-span-4' }, [
      h('h3', { class: 'text-lg font-semibold text-gray-800 mb-4' }, 'Fluxo de Caixa Geral'),
      h('div', { class: 'grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6' }, [
        h('div', { class: 'bg-green-100 p-4 rounded-lg' }, [
          h('p', { class: 'text-sm text-green-700 font-medium' }, 'Total Entradas'),
          h('p', { class: 'text-2xl font-bold text-gray-900' }, `R$ ${this.totalEntradas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
        ]),
        h('div', { class: 'bg-red-100 p-4 rounded-lg' }, [
          h('p', { class: 'text-sm text-red-700 font-medium' }, 'Total Saídas'),
          h('p', { class: 'text-2xl font-bold text-gray-900' }, `R$ ${this.totalSaidas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
        ]),
        h('div', { class: 'bg-blue-100 p-4 rounded-lg' }, [
          h('p', { class: 'text-sm text-blue-700 font-medium' }, 'Saldo'),
          h('p', { class: ['text-2xl font-bold', this.saldo >= 0 ? 'text-gray-900' : 'text-red-600'] }, `R$ ${this.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
        ])
      ]),
      h('h4', { class: 'text-md font-semibold text-gray-800 mb-3' }, 'Transações Filtradas'),
      h('div', { class: 'space-y-3 max-h-48 overflow-y-auto pr-2' },
        this.data.length > 0 ? this.data.map(createItem) : [
          h('p', { class: 'text-gray-500 text-center p-4' }, 'Nenhuma transação encontrada para os filtros selecionados.')
        ]
      )
    ]);
  }
};

// GrandTotalsDashboard
const GrandTotalsDashboard = {
  name: 'GrandTotalsDashboard',
  props: ['dinheiro', 'cartao', 'pix', 'totalGeral'],
  render(h) {
    const createCard = (icon, title, value) =>
      h('div', { class: 'bg-blue-50 p-4 rounded-lg flex items-center gap-4' }, [
        h('i', { attrs: { 'data-lucide': icon }, class: 'w-8 h-8 text-blue-600' }),
        h('div', [
          h('p', { class: 'text-sm text-blue-700 font-medium' }, title),
          h('p', { class: 'text-2xl font-bold text-gray-900' }, `R$ ${parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
        ])
      ]);

    return h('div', { class: 'col-span-1 md:col-span-2 lg:col-span-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-md' }, [
      h('h3', { class: 'text-lg font-semibold text-gray-800 mb-4' }, 'Totais Gerais de Recebimentos'),
      h('div', { class: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4' }, [
        createCard('dollar-sign', 'Dinheiro', this.dinheiro),
        createCard('credit-card', 'Cartão de Crédito', this.cartao),
        createCard('qr-code', 'PIX', this.pix),
        h('div', { class: 'bg-blue-600 text-white p-4 rounded-lg flex items-center gap-4' }, [
          h('div', { class: 'text-3xl font-bold' }, 'Σ'),
          h('div', [
            h('p', { class: 'text-sm font-medium' }, 'Total Geral'),
            h('p', { class: 'text-2xl font-bold' }, `R$ ${this.totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
          ])
        ])
      ])
    ]);
  }
};

// Componente Principal
export default {
  name: 'App',
  components: {
    // Componentes locais
    CaixaStatus,
    PaymentBreakdown,
    SectionCard,
    FluxoCaixaCard,
    GrandTotalsDashboard,
  },
  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0'); // meses começam em 0
    const dd = String(today.getDate()).padStart(2, '0');

    return {
      filters: {
        date: `${yyyy}-${mm}-${dd}`,
        agency: 'all',
        operator: 'all'
      },
      displayData: {
        entradasSaidas: [],
        passagens: { quantidade: 0, dinheiro: 0, cartao: 0, pix: 0 },
        encomendas: { quantidade: 0, dinheiro: 0, cartao: 0, pix: 0 },
        excessoBagagem: { quantidade: 0, dinheiro: 0, cartao: 0, pix: 0 },
        avulsas: { quantidade: 0, entradas: { dinheiro: 0, cartao: 0, pix: 0 }, saidas: 0, totalEntradas: 0 },
      },
      caixaStatus: null,
      agencias: [],
      operadores: [],
    };
  },
  computed: {
    totalPassagens() {
      const { dinheiro, cartao, pix } = this.displayData.passagens;
      return dinheiro + cartao + pix;
    },
    totalEncomendas() {
      const { dinheiro, cartao, pix } = this.displayData.encomendas;
      return dinheiro + cartao + pix;
    },
    totalBagagem() {
      const { dinheiro, cartao, pix } = this.displayData.excessoBagagem;
      return dinheiro + cartao + pix;
    },
    grandTotalDinheiro() {
      return this.displayData.passagens.dinheiro + this.displayData.encomendas.dinheiro + this.displayData.excessoBagagem.dinheiro + this.displayData.avulsas.entradas.dinheiro;
    },
    grandTotalCartao() {
      return this.displayData.passagens.cartao + this.displayData.encomendas.cartao + this.displayData.excessoBagagem.cartao + this.displayData.avulsas.entradas.cartao;
    },
    grandTotalPix() {
      return this.displayData.passagens.pix + this.displayData.encomendas.pix + this.displayData.excessoBagagem.pix + this.displayData.avulsas.entradas.pix;
    },
    grandTotalGeral() {
      return this.grandTotalDinheiro + this.grandTotalCartao + this.grandTotalPix;
    },
    operadoresFiltrados() {
      if (this.filters.agency === 'all') return this.operadores;
      return this.operadores.filter(op => op.id_agencia === this.filters.agency);
    }
  },
  watch: {
    'filters.agency'(newAgency, oldAgency) {
      if (newAgency !== oldAgency) {
        this.filters.operator = 'all';
      }
    },
    filters: {
      handler() {
        this.updateDashboardData();
      },
      deep: true,
    }
  },
  methods: {
    resetFilters() {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0'); // meses começam em 0
      const dd = String(today.getDate()).padStart(2, '0');

      this.filters = { date: `${yyyy}-${mm}-${dd}`, agency: 'all', operator: 'all' };
    },
    calculateCategoryTotals(transactions, category) {
      const filtered = transactions.filter(t => t.categoria === category);
      const totals = {
        quantidade: filtered.length,
        dinheiro: 0,
        cartao: 0,
        pix: 0,
      };

      filtered.forEach(t => {
        if (t.formaPagamento === "1") {
          totals.dinheiro += parseFloat(t.valor);
        } else if (t.formaPagamento === "2") {
          totals.cartao += parseFloat(t.valor);
        } else if (t.formaPagamento === "4") {
          totals.pix += parseFloat(t.valor);
        }
      });

      return totals;
    },
    calculateAvulsasTotals(transactions) {
      const avulsas = transactions.filter(t => t.categoria === 'avulsa');
      const totals = {
        quantidade: avulsas.length,
        entradas: { dinheiro: 0, cartao: 0, pix: 0 },
        saidas: 0,
      };

      avulsas.forEach(t => {
        if (t.tipo === 'entrada') {
          if (t.formaPagamento === "1") {
            totals.entradas.dinheiro += parseFloat(t.valor);
          } else if (t.formaPagamento === "2") {
            totals.entradas.cartao += parseFloat(t.valor);
          } else if (t.formaPagamento === "4") {
            totals.entradas.pix += parseFloat(t.valor);
          }
        } else if (t.tipo === 'saida') {
          totals.saidas += parseFloat(t.valor);
        }
      });

      const totalEntradas = totals.entradas.dinheiro + totals.entradas.cartao + totals.entradas.pix;

      return { ...totals, totalEntradas };
    },
    async updateDashboardData() {
      const response = await this.$http.get("/dashboard/finance", {
        params: {
          date: this.filters.date,
          agency: this.filters.agency,
          operator: this.filters.operator
        }
      });

      this.caixaStatus = response.data.caixaStatus || null;
      this.displayData = {
        entradasSaidas: response.data.entradasSaidas || [],
        passagens: this.calculateCategoryTotals(response.data.entradasSaidas || [], 'passagem'),
        encomendas: this.calculateCategoryTotals(response.data.entradasSaidas || [], 'encomenda'),
        excessoBagagem: this.calculateCategoryTotals(response.data.entradasSaidas || [], 'bagagem'),
        avulsas: this.calculateAvulsasTotals(response.data.entradasSaidas || [])
      };
    },
    async getAgencias() {
      const response = await this.$http.get("/agencias");
      this.agencias = response.data;
    },
    async getOperadores() {
      const response = await this.$http.get("/operadores");
      this.operadores = response.data;
    },
  },
  created() {
    this.getAgencias();
    this.getOperadores();
    this.updateDashboardData();
  },
  mounted() {
    if (window.lucide) {
      window.lucide.createIcons(); // Executa depois que o DOM existe
    }
  },
  updated() {
    if (window.lucide) {
      window.lucide.createIcons();
    }
  },
};
</script>
